import { Section } from '@/components/shared';
import { Button, Flex, Heading, Image, Text } from '@chakra-ui/react';
import { useKeenSlider } from 'keen-slider/react';
import { useTranslation } from 'react-i18next';
import { steps } from '../constants';
import { useTrackCurrentSlide, useTrackLoadedState } from '../hooks';
import type { ISectionItemWithImage } from '../types';
import { SlideDots } from './SlideDots';
import React from 'react';

const Step: React.FC<ISectionItemWithImage> = (props): JSX.Element => {
  const { content, image, srcSet, title } = props;
  return (
    <Flex
      className="keen-slider__slide"
      direction="column"
      gap={4}
      justifyContent="space-between"
      minW={{ base: 300, md: '33.33%' }}
      p={{ base: 2, md: 4 }}
      textAlign={{ base: 'center', md: 'left' }}
    >
      <Image src={image} srcSet={srcSet} />

      <Heading
        fontSize={{ base: 'h2.base', md: 'h2.md' }}
        fontWeight={{ base: 'normal', md: 'medium' }}
      >
        {title}
      </Heading>
      <Text fontSize={{ base: 'medium', md: 'large' }}>{content}</Text>
    </Flex>
  );
};

export const Steps = (): JSX.Element => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useTrackCurrentSlide();
  const [isLoaded, setLoaded] = useTrackLoadedState();

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    disabled: false,
    mode: 'free-snap',
    slides: {
      perView: 'auto',
      origin: 'center',
      spacing: 15,
    },
    breakpoints: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '(min-width: 48em)': {
        disabled: true,
      },
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  return (
    <Section
      headingProps={{ mt: { base: 12, md: '120px' } }}
      id="how-it-works"
      title={t('LANDING.STEPS.SECTION_TITLE')}
    >
      <Flex alignItems="flex-start" className="keen-slider" ref={sliderRef}>
        {steps.map(({ content, title, ...rest }) => (
          <Step content={t(content)} key={rest.id} title={t(title)} {...rest} />
        ))}
      </Flex>
      {isLoaded && instanceRef.current && (
        <SlideDots currentSlide={currentSlide} slider={instanceRef} />
      )}
      <Button as="a" href="/quote/user" variant="mbePrimary">
        {t('LANDING.STEPS.SECTION_CTA')}
      </Button>
    </Section>
  );
};
